import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApplicationError } from '../../../model/error';
import { ErrorDialogData } from '../../../model/error-dialog-data';
import { ApplicationErrorHandler } from '../../../service/error/application-error-handler';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-error-modal',
    templateUrl: './error-modal.component.html',
    styleUrls: ['./error-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatIcon, MatTooltip]
})
export class ErrorModalComponent {
  private readonly applicationErrorHandler = inject(ApplicationErrorHandler);
  private readonly dialogRef = inject<MatDialogRef<ErrorModalComponent>>(MatDialogRef);
  private readonly data = inject<ErrorDialogData>(MAT_DIALOG_DATA);

  private readonly defaultError: ApplicationError = {
    technicalName: 'The error code is either invalid or nonexistant, which I guess is the same thing here',
    friendlyName: 'Unknown Error',
    errorCode: 0,
    actionText: "",
    action: () => {},
    triggerActionImmediately: false
  };

  public readonly id: string | undefined = this.data.sentryId;
  public readonly error: ApplicationError =
    this.applicationErrorHandler.getErrorByErrorIdentifier(this.parseErrorCode()) || this.defaultError;

  public readonly technicalDetailsPanelOpen = signal(false);
  public readonly tooltipText = signal('Copy to clipboard');

  public triggerErrorAction(): void {
    if (this.error.action) {
      this.error.action();
    }

    this.dialogRef.close();
  }

  public toggleTechnicalDetailsPane(): void {
    this.technicalDetailsPanelOpen.update(open => !open);
  }

  public copy(e: Event): void {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(this.id as string).then(() => {
      this.tooltipText.set('Copied');
    }).catch(() => {
      this.tooltipText.set('Copy failed. Please select the code and copy it manually.');
    });
  }

  public setTooltipTextDefault(): void {
    setTimeout(() => this.tooltipText.set('Copy to clipboard'), 50);
  }

  private parseErrorCode(): number {
    const parsed = Number(this.data.errorCode);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
}
